import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Wand2, Tag, Wrench, ListChecks, Plus, Trash2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const tok = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

/**
 * Renders the LIVE Ari-tailored workspace tools on a real job: custom status
 * labels, equipment tracking (job's customer), and a required checklist.
 * Each section appears ONLY if the company approved that capability.
 */
export default function WorkspaceJobPanel({ appointmentId, customerId }) {
  const [job, setJob] = useState(null);
  const [equip, setEquip] = useState(null);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ make: '', model: '', serial: '' });

  const load = useCallback(async () => {
    if (!appointmentId) return;
    try {
      const h = { headers: authHeader() };
      const j = await axios.get(`${API}/workspace-surfaces/job/${appointmentId}`, h);
      setJob(j.data);
      if (j.data.company_id && customerId) {
        const e = await axios.get(`${API}/workspace-surfaces/customer/${customerId}/equipment`, h);
        setEquip(e.data);
      }
    } catch { /* surfaces are optional */ }
  }, [appointmentId, customerId]);

  useEffect(() => { load(); }, [load]);

  if (!job) return null;
  const hasLabels = (job.custom_status_labels || []).length > 0;
  const equipEnabled = equip?.enabled;
  const checklistActive = job.required_checklist_active && (job.checklist || []).length > 0;
  if (!hasLabels && !equipEnabled && !checklistActive) return null;

  const setLabel = async (label) => {
    setBusy('label');
    try {
      await axios.post(`${API}/workspace-surfaces/job/${appointmentId}/status-label`,
        { label }, { headers: authHeader() });
      await load();
    } finally { setBusy(''); }
  };

  const addEquip = async () => {
    if (!form.make && !form.model && !form.serial) return;
    setBusy('equip');
    try {
      await axios.post(`${API}/workspace-surfaces/customer/${customerId}/equipment`,
        form, { headers: authHeader() });
      setForm({ make: '', model: '', serial: '' });
      await load();
    } finally { setBusy(''); }
  };

  const delEquip = async (id) => {
    setBusy(`del-${id}`);
    try {
      await axios.delete(`${API}/workspace-surfaces/customer/${customerId}/equipment/${id}`,
        { headers: authHeader() });
      await load();
    } finally { setBusy(''); }
  };

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4 space-y-4" data-testid="workspace-job-panel">
      <div className="flex items-center gap-2 text-violet-700">
        <Wand2 className="w-4 h-4" /><span className="text-sm font-semibold">Ari Workspace Tools</span>
      </div>

      {hasLabels && (
        <div data-testid="ws-status-label">
          <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Status label</p>
          <div className="flex flex-wrap gap-2 items-center">
            {(job.custom_status_labels || []).map(l => (
              <button key={l.label} disabled={busy === 'label'} onClick={() => setLabel(l.label)}
                data-testid={`ws-label-${l.label.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${job.workspace_status_label === l.label ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}>
                {l.label}
              </button>
            ))}
            {job.workspace_status_label && (
              <button disabled={busy === 'label'} onClick={() => setLabel('')} data-testid="ws-label-clear"
                className="text-xs text-slate-400 underline">clear</button>
            )}
          </div>
        </div>
      )}

      {checklistActive && (
        <div data-testid="ws-checklist">
          <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Required checklist</p>
          <ul className="space-y-1">
            {(job.checklist || []).map((it, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded border ${it.completed ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`} />
                {it.task}{it.required && <span className="text-[10px] text-rose-500">required</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {equipEnabled && (
        <div data-testid="ws-equipment">
          <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Equipment</p>
          <div className="space-y-1.5 mb-2">
            {(equip.equipment || []).map(eq => (
              <div key={eq.id} className="flex items-center justify-between text-xs bg-white rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid={`ws-equip-${eq.id}`}>
                <span className="text-slate-700">{[eq.make, eq.model].filter(Boolean).join(' ')} {eq.serial && <span className="text-slate-400">· SN {eq.serial}</span>}</span>
                <button disabled={busy === `del-${eq.id}`} onClick={() => delEquip(eq.id)} data-testid={`ws-equip-del-${eq.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {(equip.equipment || []).length === 0 && <p className="text-xs text-slate-400">No equipment yet.</p>}
          </div>
          <div className="flex gap-1.5">
            {['make', 'model', 'serial'].map(f => (
              <input key={f} value={form[f]} onChange={e => setForm({ ...form, [f]: e.target.value })}
                placeholder={f} data-testid={`ws-equip-${f}`}
                className="flex-1 min-w-0 px-2 py-1 text-xs rounded-lg border border-slate-200 focus:border-violet-400 outline-none" />
            ))}
            <button disabled={busy === 'equip'} onClick={addEquip} data-testid="ws-equip-add"
              className="px-2.5 py-1 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 flex items-center gap-1">
              {busy === 'equip' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
